import React, { useCallback, useEffect, useRef, useState } from "react";
import { selectClass } from "../fields/fieldStyles";
import FieldError from "../fields/FieldError";
import Label from "../fields/Label";
import TextInput from "../fields/TextInput";
import { MdSearch, MdArrowBack } from "react-icons/md";
import { ComponentAmount, IngredientEntity } from "../../types/recipe";
import { IngredientService } from "../../services/ingredientService";
import {
  limitPortionText,
  PORTION_MIN_G,
  PORTION_MAX_G,
} from "../../utils/recipeMacros";
import {
  GRAM_UNIT,
  QUANTITY_MAX,
  QUANTITY_MIN,
  amountAfterUnitChange,
  ensureUnits,
  findUnit,
  formatAmountWithGrams,
  formatUnitAmount,
  formatUnitLabel,
  isGramUnit,
  resolveAmount,
  resolveGrams,
} from "../../utils/measurementUnits";
import { formatNumber } from "../../utils/formatters";
import ModalShell from "../modals/ModalShell";
import LoadingSpinner from "../common/LoadingSpinner";
import { useLatestRequest } from "../../hooks/useLatestRequest";
import { formatNutrientAmount } from "./nutrientMeta";

interface IngredientPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPick: (ingredient: IngredientEntity, amount: ComponentAmount) => void;
}

const DEBOUNCE_MS = 500;

const GRAMS_RANGE_MESSAGE = `Portion must be between ${PORTION_MIN_G} and ${formatNumber(
  PORTION_MAX_G
)} g`;
const QUANTITY_RANGE_MESSAGE = `Quantity must be between ${QUANTITY_MIN} and ${formatNumber(
  QUANTITY_MAX
)}`;

const IngredientPickerModal: React.FC<IngredientPickerModalProps> = ({
  isOpen,
  onClose,
  onPick,
}) => {
  const [query, setQuery] = useState("");
  const [items, setItems] = useState<IngredientEntity[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [isListReady, setIsListReady] = useState(false);

  const [selected, setSelected] = useState<IngredientEntity | null>(null);
  const [portionInput, setPortionInput] = useState("");
  const [unitLabel, setUnitLabel] = useState(GRAM_UNIT.label);
  const [portionError, setPortionError] = useState<string | null>(null);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const listedTermRef = useRef<string | null>(null);
  const startRequest = useLatestRequest();

  const runSearch = useCallback(
    async (term: string, pageNum: number, append: boolean) => {
      const isCurrent = startRequest();
      listedTermRef.current = term;
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      setError(null);
      const result = await IngredientService.searchIngredients(term, pageNum);
      if (!isCurrent()) return;
      if (result.success && result.data) {
        const data = result.data;
        setItems((prev) => {
          if (!append) return data.items;
          const existingIds = new Set(prev.map((i) => i.id));
          return [...prev, ...data.items.filter((i) => !existingIds.has(i.id))];
        });
        setPage(data.page);
        setHasMore(data.hasMore);
      } else {
        setError(result.error || "Failed to search ingredients");
        if (!append) setItems([]);
        setHasMore(false);
      }
      setLoading(false);
      setLoadingMore(false);
      setIsListReady(true);
    },
    [startRequest]
  );

  useEffect(() => {
    if (isOpen) {
      setQuery("");
      setItems([]);
      setError(null);
      setPage(1);
      setHasMore(false);
      setIsListReady(false);
      setSelected(null);
      setPortionInput("");
      setUnitLabel(GRAM_UNIT.label);
      setPortionError(null);
      runSearch("", 1, false);
    }
  }, [isOpen, runSearch]);

  // Debounced search
  useEffect(() => {
    if (!isOpen || selected || !isListReady) return;
    if (query === listedTermRef.current) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      runSearch(query, 1, false);
    }, DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, isOpen, selected, isListReady, runSearch]);

  // Grams stay the default; the food's own measures are offered beside them.
  const units = selected
    ? ensureUnits(selected.measurement_units)
    : [GRAM_UNIT];
  const unit = findUnit(units, unitLabel) ?? GRAM_UNIT;
  const isGrams = isGramUnit(unit);

  const typed = Number(portionInput);
  const hasTyped = portionInput.trim().length > 0 && Number.isFinite(typed);
  const preview =
    !isGrams && hasTyped && typed > 0
      ? `${formatUnitAmount(typed, unit.label)} = ${formatAmountWithGrams(
          resolveGrams(typed, unit),
          null,
          null
        )}`
      : null;

  const handleSelect = (ingredient: IngredientEntity) => {
    setSelected(ingredient);
    const fallback =
      ingredient.default_serving_grams && ingredient.default_serving_grams > 0
        ? ingredient.default_serving_grams
        : 100;
    setPortionInput(String(Math.round(fallback * 100) / 100));
    setUnitLabel(GRAM_UNIT.label);
    setPortionError(null);
  };

  const handleUnitChange = (nextLabel: string) => {
    const next = findUnit(units, nextLabel) ?? GRAM_UNIT;
    setPortionInput(amountAfterUnitChange(portionInput, unit, next));
    setUnitLabel(next.label);
    setPortionError(null);
  };

  const handlePortionChange = (text: string) => {
    setPortionInput(
      limitPortionText(text, isGrams ? PORTION_MAX_G : QUANTITY_MAX)
    );
  };

  const handleConfirmPortion = () => {
    if (!selected) return;
    const value = parseFloat(portionInput);

    if (isGrams) {
      if (
        !Number.isFinite(value) ||
        value < PORTION_MIN_G ||
        value > PORTION_MAX_G
      ) {
        setPortionError(GRAMS_RANGE_MESSAGE);
        return;
      }
      onPick(selected, {
        portion_grams: value,
        quantity: null,
        measurement_unit_label: null,
      });
      return;
    }

    if (
      !Number.isFinite(value) ||
      value < QUANTITY_MIN ||
      value > QUANTITY_MAX
    ) {
      setPortionError(QUANTITY_RANGE_MESSAGE);
      return;
    }
    const amount = resolveAmount(value, unit);
    // The same weight limits apply whichever unit the amount was typed in.
    if (
      amount.portion_grams < PORTION_MIN_G ||
      amount.portion_grams > PORTION_MAX_G
    ) {
      setPortionError(GRAMS_RANGE_MESSAGE);
      return;
    }
    onPick(selected, amount);
  };

  const handleBackToList = () => {
    setSelected(null);
    setPortionError(null);
  };

  if (!isOpen) return null;

  return (
    // depth="nested" because this opens on top of RecipeModal.
    <ModalShell
      isOpen
      onClose={onClose}
      title={selected ? "Set portion" : "Add ingredient"}
      size="lg"
      layout="column"
      depth="nested"
    >
      {selected ? (
        /* Portion step */
        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4">
          <button
            type="button"
            onClick={handleBackToList}
            className="inline-flex w-fit items-center text-sm text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
          >
            <MdArrowBack className="mr-1 h-4 w-4" />
            Back to search
          </button>

          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">How much</p>
            <p className="text-lg font-semibold text-navy-700 dark:text-white">
              {selected.name}?
            </p>
          </div>

          <div>
            <div className="flex gap-3">
              <div className="min-w-0 flex-1">
                <Label htmlFor="ingredient-portion">
                  {isGrams ? "Portion (grams)" : "Quantity"}
                </Label>
                <TextInput
                  id="ingredient-portion"
                  type="text"
                  inputMode="decimal"
                  maxLength={10}
                  autoFocus
                  value={portionInput}
                  invalid={!!portionError}
                  onChange={(e) => handlePortionChange(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleConfirmPortion();
                    }
                  }}
                  placeholder={isGrams ? "100" : "1"}
                />
              </div>
              <div className="w-36 shrink-0">
                <Label htmlFor="ingredient-unit">Unit</Label>
                <select
                  id="ingredient-unit"
                  value={unit.label}
                  onChange={(e) => handleUnitChange(e.target.value)}
                  className={selectClass()}
                >
                  {units.map((u) => (
                    <option key={u.label} value={u.label}>
                      {formatUnitLabel(u.label)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {preview && (
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                {preview}
              </p>
            )}
            <FieldError>{portionError}</FieldError>
          </div>

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={handleBackToList}
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 dark:border-gray-600 dark:bg-navy-700 dark:text-gray-300 dark:hover:bg-navy-600"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirmPortion}
              disabled={portionInput.trim().length === 0}
              className="rounded-md bg-biafit-primary px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-biafit-secondary disabled:opacity-50"
            >
              Add
            </button>
          </div>
        </div>
      ) : (
        /* Search list step */
        <div className="flex min-h-0 flex-1 flex-col">
          <div className="border-b border-gray-200 p-4 dark:border-gray-800">
            <div className="relative">
              <MdSearch className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
              <TextInput
                type="text"
                autoFocus
                aria-label="Search ingredients"
                // Read-only rather than disabled so ModalShell can focus it.
                readOnly={!isListReady}
                aria-busy={!isListReady}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="pl-10"
                placeholder="Search ingredients..."
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            {error && <p className="mb-3 text-sm text-red-600">{error}</p>}

            {loading ? (
              <div className="flex justify-center py-8">
                <LoadingSpinner bare size="lg" />
              </div>
            ) : items.length === 0 ? (
              <p className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                No ingredients found.
              </p>
            ) : (
              <div className="space-y-2">
                {items.map((ingredient) => (
                  <button
                    key={ingredient.id}
                    type="button"
                    onClick={() => handleSelect(ingredient)}
                    className="flex w-full items-center justify-between rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-left transition-colors hover:border-biafit-primary dark:border-gray-700 dark:bg-gray-800 dark:hover:border-biafit-primary"
                  >
                    <span className="min-w-0 flex-1 pr-2 font-medium text-navy-700 dark:text-white">
                      {ingredient.name}
                    </span>
                    <span className="shrink-0 text-sm text-gray-700 dark:text-gray-300">
                      {formatNutrientAmount(
                        "calories",
                        ingredient.calories_kcal_per_100g
                      )}{" "}
                      kcal/100g
                    </span>
                  </button>
                ))}

                {hasMore && (
                  <button
                    type="button"
                    disabled={loadingMore}
                    onClick={() => runSearch(query, page + 1, true)}
                    className="mt-2 w-full rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-navy-700"
                  >
                    {loadingMore ? "Loading..." : "Load more"}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </ModalShell>
  );
};

export default IngredientPickerModal;
